import React, { Fragment } from 'react';
import { copyFor } from 'config';
import { formatAsMoney } from '@perch-labs/warbler';
import { FlexCrate } from '@perch-labs/shrike';
import { CorrectionLineItem } from './correction-line-item';
import {
  StyledList,
  StyledSpacer,
  StyledHeading,
  StyledContainer,
} from './line-items.style';
import { VnmSolarChargeLineItem } from './vnm-solar-charge-line-item';
import { VnmSolarChargePreCreditLineItem } from './vnm-solar-charge-pre-credit-line-item';
import { VnmSolarChargeDelayedCreditLineItem } from './vnm-solar-charge-delayed-credit-line-item';
import { LocalUtilityChargeWithVnmSolarLineItem } from './local-utility-charge-with-vnm-solar-line-item';
import { LocalUtilityChargeWithoutVnmSolarLineItem } from './local-utility-charge-without-vnm-solar-line-item';

import { ExtrasLineItem } from './extras-line-item';

const getCopy = copyFor('billing.statement.lineItems');

interface Props {
  lineItemsFormatted: Array<FormattedLineItem>;
  statementTotal: number;
  utilityName?: string;
  includeTooltips?: boolean;
  includeSupportingText?: boolean;
}

const excludedTypesFromExtras: Array<LineItemKey> = [
  'credit_adjustment',
  'debit_adjustment',
  'local_utility_charge',
  'overcharge_correction',
  'undercharge_correction',
  'vnm_solar_charge',
];

export const LineItems = (props: Props) => {
  const {
    lineItemsFormatted,
    includeTooltips = true,
    includeSupportingText = false,
    statementTotal,
    utilityName,
  } = props;

  const getVnmSolarChargeLineItem = (lineItem: SolarLineItem) => {
    if (lineItem.isPreCredit) {
      return <VnmSolarChargePreCreditLineItem utilityName={utilityName} />;
    } else if (lineItem.isDelayedCredit) {
      return <VnmSolarChargeDelayedCreditLineItem />;
    } else {
      return (
        <VnmSolarChargeLineItem
          lineItem={lineItem}
          utilityName={utilityName}
          includeTooltips={includeTooltips}
          includeSupportingText={includeSupportingText}
        />
      );
    }
  };

  const getFormattedLineItemContent = (lineItem: FormattedLineItem) => {
    switch (lineItem.key) {
      case 'vnm_solar_charge':
        return getVnmSolarChargeLineItem(lineItem as SolarLineItem);
      case 'local_utility_charge':
        return (lineItem as UtilityLineItem).hasCorrespondingSolarCharge ? (
          <LocalUtilityChargeWithVnmSolarLineItem
            lineItem={lineItem as UtilityLineItem}
            utilityName={utilityName}
            includeTooltips={includeTooltips}
            includeSupportingText={includeSupportingText}
          />
        ) : (
          <LocalUtilityChargeWithoutVnmSolarLineItem
            lineItem={lineItem as UtilityLineItem}
            utilityName={utilityName}
            includeTooltips={includeTooltips}
            includeSupportingText={includeSupportingText}
          />
        );
      case 'credit_adjustment':
      case 'debit_adjustment':
      case 'overcharge_correction':
      case 'undercharge_correction':
        return (
          <CorrectionLineItem
            lineItem={lineItem as CorrectionLineItem}
            includeTooltips={includeTooltips}
            includeSupportingText={includeSupportingText}
          />
        );
      default:
        return null;
    }
  };

  return (
    <FlexCrate flexDirection="column" dataAttributes={{ testid: 'line-items' }}>
      <StyledList>
        {lineItemsFormatted.map(lineItem => {
          return (
            <Fragment key={lineItem.id}>
              {getFormattedLineItemContent(lineItem)}
            </Fragment>
          );
        })}
        <ExtrasLineItem
          lineItems={lineItemsFormatted.filter(
            lineItem => !excludedTypesFromExtras.includes(lineItem.key)
          )}
          includeTooltips={includeTooltips}
          includeSupportingText={includeSupportingText}
        />
      </StyledList>
      <StyledSpacer />
      <StyledContainer>
        <StyledHeading>{getCopy('total')}</StyledHeading>
        <StyledHeading>
          {formatAsMoney(statementTotal, { conversion: 'fromCents' })}
        </StyledHeading>
      </StyledContainer>
    </FlexCrate>
  );
};
